import React, { useState, useEffect, useMemo } from 'react';
import { X, Truck, Warehouse, Edit3, Clock, Package, AlertCircle, Search, Check, FileText } from 'lucide-react';
import { bilmottakService, BilmottakType, CreateBilmottakRequest, CreateBilmottakResponse } from '../../../../services/bilmottakService';
import { WarehouseService } from '../../../../services/warehouseService';
import { pOrdersService } from '../../../../services/pOrdersService';
import { Warehouse as WarehouseType } from '../../../../types/warehouse';
import { POrdersDto } from '../../../../types/porders';
import { useTranslation } from '../../../../contexts/I18nProvider';
import { useWarehouse } from '../../../../contexts/WarehouseContext';

interface NewBilmottakModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSessionCreated: (batchId: string, name: string, warehouse: string) => void;
}

const warehouseService = new WarehouseService();

/**
 * Modal for creating a new Bilmottak (Truck Receiving) session - Modus 4
 * Supports both predefined session types and free-text custom names.
 * Includes PO selection for linking orders to the session.
 * Uses purple color scheme (#8B5CF6) to distinguish from other receiving modes.
 */
export const NewBilmottakModal: React.FC<NewBilmottakModalProps> = ({
  isOpen,
  onClose,
  onSessionCreated
}) => {
  const { t } = useTranslation();
  const { currentWarehouse } = useWarehouse();

  // Form state
  const [sessionName, setSessionName] = useState('');
  const [selectedType, setSelectedType] = useState<BilmottakType>(BilmottakType.Custom);
  const [selectedWarehouse, setSelectedWarehouse] = useState('');
  const [description, setDescription] = useState('');

  // PO selection state
  const [availableOrders, setAvailableOrders] = useState<POrdersDto[]>([]);
  const [selectedOrderNos, setSelectedOrderNos] = useState<Set<number>>(new Set());
  const [orderSearchTerm, setOrderSearchTerm] = useState('');
  const [loadingOrders, setLoadingOrders] = useState(false);

  // UI state
  const [warehouses, setWarehouses] = useState<WarehouseType[]>([]);
  const [loading, setLoading] = useState(false);
  const [loadingWarehouses, setLoadingWarehouses] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Helper function to get localized type name
  const getLocalizedTypeName = (type: BilmottakType): string => {
    switch (type) {
      case BilmottakType.Morgenleveranse:
        return t('warehouse.modules.consolidatedReceiving.morningDelivery');
      case BilmottakType.Ettermiddagsleveranse:
        return t('warehouse.modules.consolidatedReceiving.afternoonDelivery');
      case BilmottakType.Ekspresslevering:
        return t('warehouse.modules.consolidatedReceiving.expressDelivery');
      case BilmottakType.Returvarer:
        return t('warehouse.modules.consolidatedReceiving.returnGoods');
      case BilmottakType.CrossDock:
        return t('warehouse.modules.consolidatedReceiving.crossDock');
      case BilmottakType.Custom:
        return t('warehouse.modules.consolidatedReceiving.custom');
      default:
        return type;
    }
  };

  // Predefined session types
  const predefinedTypes = [
    { type: BilmottakType.Morgenleveranse, icon: Clock, description: t('warehouse.modules.consolidatedReceiving.morningDeliveryDescription') },
    { type: BilmottakType.Ettermiddagsleveranse, icon: Clock, description: t('warehouse.modules.consolidatedReceiving.afternoonDeliveryDescription') },
    { type: BilmottakType.Ekspresslevering, icon: Package, description: t('warehouse.modules.consolidatedReceiving.expressDeliveryDescription') },
    { type: BilmottakType.Returvarer, icon: Package, description: t('warehouse.modules.consolidatedReceiving.returnGoodsDescription') },
    { type: BilmottakType.CrossDock, icon: Truck, description: t('warehouse.modules.consolidatedReceiving.crossDockDescription') },
  ];

  // Load warehouses on mount
  useEffect(() => {
    const loadWarehouses = async () => {
      try {
        setLoadingWarehouses(true);
        const data = await warehouseService.getWarehouses();
        setWarehouses(data);

        // Auto-select first warehouse if available
        if (data.length > 0 && !selectedWarehouse) {
          setSelectedWarehouse(data[0].warehouseID);
        }
      } catch (err) {
        console.error('Error loading warehouses:', err);
        setError(t('warehouse.modules.consolidatedReceiving.couldNotLoadWarehouses'));
      } finally {
        setLoadingWarehouses(false);
      }
    };

    if (isOpen) {
      loadWarehouses();
    }
  }, [isOpen]);

  // Load available POs when warehouse changes
  useEffect(() => {
    const loadOrders = async () => {
      if (!selectedWarehouse) {
        setAvailableOrders([]);
        return;
      }

      try {
        setLoadingOrders(true);
        const orders = await pOrdersService.getOrdersForReceiving(currentWarehouse || undefined);
        // Filter by selected warehouse
        const filtered = orders.filter(
          (o) => !o.warehouse || o.warehouse === selectedWarehouse
        );
        setAvailableOrders(filtered);
      } catch (err) {
        console.error('Error loading orders for receiving:', err);
      } finally {
        setLoadingOrders(false);
      }
    };

    if (isOpen && selectedWarehouse) {
      loadOrders();
    }
  }, [isOpen, selectedWarehouse]);

  // Reset form when modal opens
  useEffect(() => {
    if (isOpen) {
      setSessionName('');
      setSelectedType(BilmottakType.Custom);
      setDescription('');
      setError(null);
      setSelectedOrderNos(new Set());
      setOrderSearchTerm('');
    }
  }, [isOpen]);

  // Filter orders by search term
  const filteredOrders = useMemo(() => {
    if (!orderSearchTerm.trim()) return availableOrders;
    const term = orderSearchTerm.toLowerCase();
    return availableOrders.filter(
      (o) =>
        o.orderNoERP?.toLowerCase().includes(term) ||
        o.supplierName?.toLowerCase().includes(term) ||
        o.supplierNo?.toLowerCase().includes(term) ||
        String(o.orderNo).includes(term)
    );
  }, [availableOrders, orderSearchTerm]);

  // Handle type selection
  const handleTypeSelect = (type: BilmottakType) => {
    setSelectedType(type);
    if (type !== BilmottakType.Custom) {
      setSessionName(getLocalizedTypeName(type));
    }
  };

  // Toggle PO selection
  const toggleOrder = (orderNo: number) => {
    setSelectedOrderNos((prev) => {
      const next = new Set(prev);
      if (next.has(orderNo)) {
        next.delete(orderNo);
      } else {
        next.add(orderNo);
      }
      return next;
    });
  };

  // Select/deselect all visible orders
  const toggleAllVisible = () => {
    const visibleOrderNos = filteredOrders.map((o) => o.orderNo);
    const allSelected = visibleOrderNos.every((no) => selectedOrderNos.has(no));
    setSelectedOrderNos((prev) => {
      const next = new Set(prev);
      if (allSelected) {
        visibleOrderNos.forEach((no) => next.delete(no));
      } else {
        visibleOrderNos.forEach((no) => next.add(no));
      }
      return next;
    });
  };

  // Handle form submission
  const handleSubmit = async () => {
    // Validation
    if (selectedType === BilmottakType.Custom && !sessionName.trim()) {
      setError(t('warehouse.modules.consolidatedReceiving.sessionNameRequired'));
      return;
    }

    if (!selectedWarehouse) {
      setError(t('warehouse.modules.consolidatedReceiving.warehouseRequired'));
      return;
    }

    try {
      setLoading(true);
      setError(null);

      const request: CreateBilmottakRequest = {
        name: selectedType === BilmottakType.Custom ? sessionName.trim() : getLocalizedTypeName(selectedType),
        type: selectedType,
        warehouse: selectedWarehouse,
        description: description.trim() || undefined,
        selectedOrderNos: selectedOrderNos.size > 0 ? Array.from(selectedOrderNos) : undefined
      };

      const response: CreateBilmottakResponse = await bilmottakService.createSession(request);

      if (!response.success) {
        setError(response.errorMessage || t('warehouse.modules.consolidatedReceiving.couldNotCreateSession'));
        return;
      }

      // Success - notify parent and close
      const finalName = selectedType === BilmottakType.Custom ? sessionName.trim() : getLocalizedTypeName(selectedType);
      onSessionCreated(response.batchId, finalName, selectedWarehouse);
      onClose();
    } catch (err) {
      console.error('Error creating Bilmottak session:', err);
      setError(t('warehouse.modules.consolidatedReceiving.errorCreatingSession'));
    } finally {
      setLoading(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
        {/* Header - Purple theme */}
        <div className="bg-purple-600 text-white px-6 py-4 rounded-t-lg flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Truck className="h-6 w-6" />
            <div>
              <h2 className="text-lg font-semibold">{t('warehouse.modules.consolidatedReceiving.sessionTitle')}</h2>
              <p className="text-purple-100 text-sm">{t('warehouse.modules.consolidatedReceiving.sessionSubtitle')}</p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="text-white hover:bg-purple-500 rounded-full p-1 transition-colors"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        {/* Content */}
        <div className="p-6 space-y-6">
          {/* Error message */}
          {error && (
            <div className="bg-red-50 border border-red-200 rounded-lg p-3 flex items-center gap-2 text-red-700">
              <AlertCircle className="h-5 w-5 flex-shrink-0" />
              <span className="text-sm">{error}</span>
            </div>
          )}

          {/* Session type selection */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              {t('warehouse.modules.consolidatedReceiving.sessionType')}
            </label>
            <div className="grid grid-cols-2 gap-2">
              {/* Custom option */}
              <button
                type="button"
                onClick={() => handleTypeSelect(BilmottakType.Custom)}
                className={`p-3 rounded-lg border-2 text-left transition-all ${
                  selectedType === BilmottakType.Custom
                    ? 'border-purple-500 bg-purple-50'
                    : 'border-gray-200 hover:border-purple-300'
                }`}
              >
                <div className="flex items-center gap-2">
                  <Edit3 className={`h-4 w-4 ${selectedType === BilmottakType.Custom ? 'text-purple-600' : 'text-gray-500'}`} />
                  <span className={`font-medium text-sm ${selectedType === BilmottakType.Custom ? 'text-purple-700' : 'text-gray-700'}`}>
                    {t('warehouse.modules.consolidatedReceiving.custom')}
                  </span>
                </div>
                <p className="text-xs text-gray-500 mt-1">{t('warehouse.modules.consolidatedReceiving.customDescription')}</p>
              </button>

              {/* Predefined types */}
              {predefinedTypes.map(({ type, icon: Icon, description: desc }) => (
                <button
                  key={type}
                  type="button"
                  onClick={() => handleTypeSelect(type)}
                  className={`p-3 rounded-lg border-2 text-left transition-all ${
                    selectedType === type
                      ? 'border-purple-500 bg-purple-50'
                      : 'border-gray-200 hover:border-purple-300'
                  }`}
                >
                  <div className="flex items-center gap-2">
                    <Icon className={`h-4 w-4 ${selectedType === type ? 'text-purple-600' : 'text-gray-500'}`} />
                    <span className={`font-medium text-sm ${selectedType === type ? 'text-purple-700' : 'text-gray-700'}`}>
                      {getLocalizedTypeName(type)}
                    </span>
                  </div>
                  <p className="text-xs text-gray-500 mt-1 line-clamp-1">{desc}</p>
                </button>
              ))}
            </div>
          </div>

          {/* Custom name input - only shown for Custom type */}
          {selectedType === BilmottakType.Custom && (
            <div>
              <label htmlFor="sessionName" className="block text-sm font-medium text-gray-700 mb-1">
                {t('warehouse.modules.consolidatedReceiving.sessionName')} *
              </label>
              <input
                id="sessionName"
                type="text"
                value={sessionName}
                onChange={(e) => setSessionName(e.target.value)}
                placeholder={t('warehouse.modules.consolidatedReceiving.sessionNamePlaceholder')}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                autoFocus
              />
            </div>
          )}

          {/* Warehouse selection */}
          <div>
            <label htmlFor="warehouse" className="block text-sm font-medium text-gray-700 mb-1">
              <div className="flex items-center gap-2">
                <Warehouse className="h-4 w-4" />
                <span>{t('warehouse.modules.consolidatedReceiving.warehouse')} *</span>
              </div>
            </label>
            {loadingWarehouses ? (
              <div className="flex items-center gap-2 text-gray-500 text-sm py-2">
                <div className="animate-spin h-4 w-4 border-2 border-purple-500 border-t-transparent rounded-full" />
                <span>{t('warehouse.modules.consolidatedReceiving.loadingWarehouses')}</span>
              </div>
            ) : (
              <select
                id="warehouse"
                value={selectedWarehouse}
                onChange={(e) => {
                  setSelectedWarehouse(e.target.value);
                  setSelectedOrderNos(new Set()); // Reset PO selection on warehouse change
                }}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
              >
                <option value="">{t('warehouse.modules.consolidatedReceiving.selectWarehouse')}</option>
                {warehouses.map((wh) => (
                  <option key={wh.warehouseID} value={wh.warehouseID}>
                    {wh.warehouseID} - {wh.warehouseDescr || wh.warehouseID}
                  </option>
                ))}
              </select>
            )}
          </div>

          {/* Purchase Order Selection */}
          {selectedWarehouse && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                <div className="flex items-center gap-2">
                  <FileText className="h-4 w-4" />
                  <span>Koble innkjøpsordrer fra pakkseddel</span>
                </div>
              </label>
              <p className="text-xs text-gray-500 mb-2">
                Velg innkjøpsordrer som tilhører denne leveransen. Ordrelinjene vil bli tilgjengelige for mottak.
              </p>

              {/* Search */}
              <div className="relative mb-2">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
                <input
                  type="text"
                  value={orderSearchTerm}
                  onChange={(e) => setOrderSearchTerm(e.target.value)}
                  placeholder="Søk på ordrenr, leverandør..."
                  className="w-full pl-10 pr-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                />
              </div>

              {/* Order list */}
              <div className="border border-gray-200 rounded-lg max-h-48 overflow-y-auto">
                {loadingOrders ? (
                  <div className="flex items-center justify-center py-6 text-gray-500 text-sm">
                    <div className="animate-spin h-4 w-4 border-2 border-purple-500 border-t-transparent rounded-full mr-2" />
                    Laster ordrer...
                  </div>
                ) : filteredOrders.length === 0 ? (
                  <div className="flex items-center justify-center py-6 text-gray-400 text-sm">
                    {availableOrders.length === 0
                      ? 'Ingen åpne innkjøpsordrer for dette lageret'
                      : 'Ingen treff på søket'}
                  </div>
                ) : (
                  <>
                    {/* Select all header */}
                    <div className="sticky top-0 bg-gray-50 border-b border-gray-200 px-3 py-2 flex items-center gap-2">
                      <button
                        type="button"
                        onClick={toggleAllVisible}
                        className="text-xs text-purple-600 hover:text-purple-800 font-medium"
                      >
                        {filteredOrders.every((o) => selectedOrderNos.has(o.orderNo))
                          ? 'Fjern alle'
                          : 'Velg alle'}
                      </button>
                      <span className="text-xs text-gray-400 ml-auto">
                        {selectedOrderNos.size} valgt
                      </span>
                    </div>

                    {/* Orders */}
                    {filteredOrders.map((order) => {
                      const isSelected = selectedOrderNos.has(order.orderNo);
                      return (
                        <button
                          key={order.orderNo}
                          type="button"
                          onClick={() => toggleOrder(order.orderNo)}
                          className={`w-full px-3 py-2 flex items-center gap-3 text-left border-b border-gray-100 last:border-b-0 transition-colors ${
                            isSelected ? 'bg-purple-50' : 'hover:bg-gray-50'
                          }`}
                        >
                          <div className={`w-5 h-5 rounded border-2 flex items-center justify-center flex-shrink-0 transition-colors ${
                            isSelected
                              ? 'bg-purple-600 border-purple-600'
                              : 'border-gray-300'
                          }`}>
                            {isSelected && <Check className="h-3 w-3 text-white" />}
                          </div>
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-2">
                              <span className="font-medium text-sm text-gray-900">
                                {order.orderNoERP || `#${order.orderNo}`}
                              </span>
                              <span className="text-xs text-gray-400">
                                {order.numOfLines ? `${order.numOfLines} linjer` : ''}
                              </span>
                            </div>
                            <div className="text-xs text-gray-500 truncate">
                              {order.supplierName || order.supplierNo}
                              {order.orderDate && ` · ${new Date(order.orderDate).toLocaleDateString('nb-NO')}`}
                            </div>
                          </div>
                        </button>
                      );
                    })}
                  </>
                )}
              </div>
            </div>
          )}

          {/* Optional description */}
          <div>
            <label htmlFor="description" className="block text-sm font-medium text-gray-700 mb-1">
              {t('warehouse.modules.consolidatedReceiving.description')}
            </label>
            <textarea
              id="description"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder={t('warehouse.modules.consolidatedReceiving.descriptionPlaceholder')}
              rows={2}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent resize-none"
            />
          </div>

          {/* Info box */}
          <div className="bg-purple-50 border border-purple-200 rounded-lg p-3">
            <div className="flex items-start gap-2">
              <Truck className="h-5 w-5 text-purple-600 flex-shrink-0 mt-0.5" />
              <div className="text-sm text-purple-800">
                <p className="font-medium">{t('warehouse.modules.consolidatedReceiving.multiUserInfo')}</p>
                <p className="text-purple-600 mt-1">
                  {t('warehouse.modules.consolidatedReceiving.multiUserDescription')}
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="bg-gray-50 px-6 py-4 rounded-b-lg flex justify-end gap-3">
          <button
            type="button"
            onClick={onClose}
            className="px-4 py-2 text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
            disabled={loading}
          >
            {t('common.buttons.cancel')}
          </button>
          <button
            type="button"
            onClick={handleSubmit}
            disabled={loading || loadingWarehouses}
            className="px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
          >
            {loading ? (
              <>
                <div className="animate-spin h-4 w-4 border-2 border-white border-t-transparent rounded-full" />
                <span>{t('warehouse.modules.consolidatedReceiving.creating')}</span>
              </>
            ) : (
              <>
                <Truck className="h-4 w-4" />
                <span>{t('warehouse.modules.consolidatedReceiving.createSession')}</span>
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  );
};
